/**
 * useTempoAccount -- React Query wrappers over the two IMPLEMENTED per-account
 * read paths (features, recommendation) of the four the contract declares
 * under `/accounts/{accountId}`; `forecast` and `feature-provenance` are
 * spec-first, carry `openapi-route-coverage-skip` markers, and have no route.
 * Both implemented paths ran with zero callers until this page
 * (mlv2-console D2, the C3 two-path half; sprint-queue/sprint-member-liquidity-v2/
 * GO-LIVE-PLAN.md Section 13.2(b)); everything the console showed about the
 * scorecard before it was an aggregate.
 *
 * Server contract (tempo.openapi.yaml, via the memberLiquidityClient
 * openapi-fetch client):
 *
 *   GET /api/v1/tempo/accounts/{accountId}/features         LiquidityFeatureVector
 *   GET /api/v1/tempo/accounts/{accountId}/recommendation   CoverageRecommendation
 *
 * THE TWO PATHS ARE NOT DATE-SYMMETRIC, and a caller that assumes they are
 * will pair a snapshot with a recommendation computed from a different one.
 * Operation `getAccountFeatures` declares an optional `scoreDate` query
 * parameter and addresses history; operation `getCoverageRecommendation`
 * declares the `accountId` path parameter and nothing else, so it always
 * answers with the current recommendation. The join that tells a reader whether
 * the two agree is the `featureSnapshotRef` property of schema
 * `CoverageRecommendation`, which the page renders rather than hides.
 *
 * 404 handling follows the useTempoPolicy convention: the whole
 * /api/v1/tempo/* surface is gated server-side on
 * Institution.enableOverdraftPreview (404-on-OFF stealth,
 * middleware/feature-flag-gate.ts), and an account with no snapshot on the
 * requested date 404s the same way, as do the further causes the page
 * enumerates (read them from NOTHING_RECORDED_CAUSES in AccountDecisionPage,
 * which derives them from the gate and the two route handlers).
 * All resolve to `null` here and the page renders the absence as an absence.
 * The contract does not distinguish the causes, so neither does this hook.
 *
 * EVERY OTHER NON-2XX THROWS, and the two outcomes are not interchangeable: a
 * 404 is a recorded absence, a 500 is an unknown state. A caller that reads
 * `data === null` without also reading `error` will state an absence the
 * server never reported.
 *
 * Cache keys mirror the useTempoPolicy impersonation contract: the 'apex'
 * literal is a CLIENT-SIDE cache-namespace label for platform-admin sessions
 * (no tenantId claim); the backend resolves the tenant from the JWT via
 * resolveEffectiveTenantId.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import type { components } from '@aaimplatform/institutional-types/tempo';
import { memberLiquidityClient } from '../api/client';
import { useAuthStore } from '../stores/auth';

export type LiquidityFeatureVector = components['schemas']['LiquidityFeatureVector'];
export type CoverageRecommendation = components['schemas']['CoverageRecommendation'];
export type LiquidityReasonCode = components['schemas']['LiquidityReasonCode'];
export type PolicyEvidenceExport = components['schemas']['PolicyEvidenceExport'];

const FEATURES_KEY = 'tempo-account-features';
const RECOMMENDATION_KEY = 'tempo-account-recommendation';

/**
 * Normalize an openapi-fetch error body into an Error carrying the HTTP status.
 * The narrow sibling of useTempoPolicy's `toApiError`: these two operations are
 * GETs, so there is no 422 reason-coded detail list to carry.
 */
function toApiError(error: unknown, response: Response): Error & { status?: number; code?: string } {
  const envelope = error as
    | { error?: { code?: string; message?: string } | string; message?: string }
    | undefined;
  let message = `HTTP ${response.status}`;
  let code: string | undefined;
  if (envelope && typeof envelope.error === 'object' && envelope.error) {
    message = envelope.error.message ?? message;
    code = envelope.error.code;
  } else if (envelope && typeof envelope.message === 'string') {
    message = envelope.message;
  }
  return Object.assign(new Error(message), { status: response.status, code });
}

/** Client-side cache-key namespace per the impersonation contract. */
function useTenantCacheKey(): string {
  const user = useAuthStore((s) => s.user);
  return user?.tenantId ?? 'apex';
}

/**
 * The immutable feature snapshot for one account. `scoreDate` (ISO date)
 * addresses a specific snapshot; omitted, the engine returns the latest.
 * Null on the flag-off stealth 404 and on "no snapshot for that date" --
 * the contract returns one status for both.
 */
export function useAccountFeatures(accountId: string | undefined, scoreDate?: string) {
  const tenantKey = useTenantCacheKey();
  return useQuery<LiquidityFeatureVector | null, Error>({
    queryKey: [FEATURES_KEY, tenantKey, accountId ?? '', scoreDate ?? ''],
    enabled: Boolean(accountId),
    queryFn: async () => {
      const { data, error, response } = await memberLiquidityClient.GET(
        '/api/v1/tempo/accounts/{accountId}/features',
        {
          params: {
            path: { accountId: accountId as string },
            ...(scoreDate ? { query: { scoreDate } } : {}),
          },
        },
      );
      if (response.status === 404) return null;
      if (error || !data) throw toApiError(error, response);
      return data;
    },
    retry: false,
  });
}

/**
 * The current advisory recommendation for one account. The FI pay/no-pay
 * decision remains authoritative -- the words are the `getCoverageRecommendation`
 * operation's own description; this read is the engine's output and never an
 * instruction.
 *
 * No date parameter: see the header note. The returned `scoreDate` and
 * `featureSnapshotRef` say which snapshot it was computed from.
 */
export function useCoverageRecommendation(accountId: string | undefined) {
  const tenantKey = useTenantCacheKey();
  return useQuery<CoverageRecommendation | null, Error>({
    queryKey: [RECOMMENDATION_KEY, tenantKey, accountId ?? ''],
    enabled: Boolean(accountId),
    queryFn: async () => {
      const { data, error, response } = await memberLiquidityClient.GET(
        '/api/v1/tempo/accounts/{accountId}/recommendation',
        { params: { path: { accountId: accountId as string } } },
      );
      if (response.status === 404) return null;
      if (error || !data) throw toApiError(error, response);
      return data;
    },
    retry: false,
  });
}

/**
 * Examiner evidence for one account (GH #2910 deliverable 5; operator ruling
 * 2026-10-15, "Account option"). The same `exportPolicyEvidence` operation the
 * version-history tab downloads, with the optional `accountId` query parameter
 * spec 0.11.0 declares: the package carries every TEMPO policy version plus this
 * account's scored decisions under formatVersion 1.1.0, all inside
 * `integrityHash`.
 *
 * A 404 is not resolved to null here. The download is an explicit act, and the
 * caller renders the server's code: ACCOUNT_NOT_FOUND (foreign, never
 * elected, or never scored -- the contract collapses the three) or POLICY_NOT_FOUND
 * (no TEMPO version published yet).
 */
export function useDownloadAccountEvidence() {
  return useMutation<PolicyEvidenceExport, Error & { status?: number; code?: string }, string>({
    mutationFn: async (accountId: string) => {
      const { data, error, response } = await memberLiquidityClient.GET(
        '/api/v1/tempo/policy/evidence',
        { params: { query: { domain: 'TEMPO', accountId } } },
      );
      if (error || !data) throw toApiError(error, response);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `account-evidence-${accountId}-${data.exportedAt.slice(0, 10)}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      return data;
    },
  });
}
